/**
 * LiquidBrokers API Service
 * Handles API integration with LiquidBrokers broker via Backend API
 * All broker API calls are now routed through the FastAPI backend
 */

import { Trade } from "../types/trade";
import type { AccountSnapshot } from "../types/signals";
import { ApiRequestError, parseJsonOrThrow } from "../utils/apiRequestError";
import { parseApiError, parseApiErrorBody } from "../utils/parseApiError";
import type { BrokerTestConnectionResult } from "./brokerIntegrationApi";

// Backend API base URL - loaded from environment variable
// For production: Set REACT_APP_BACKEND_API_URL in .env file
// For development: Defaults to localhost:8000
const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL || "http://localhost:8000/api";

function throwLiquidBrokersClientError(message: string): never {
  throw new ApiRequestError(message, 400, {
    slug: "validation.unprocessable",
    message,
  });
}

async function throwLiquidBrokersResponseNotOk(response: Response, fallback: string): Promise<never> {
  const parsed = await parseApiError(response);
  throw new ApiRequestError(parsed.message || fallback, response.status, parsed);
}

function liquidBrokersErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiRequestError) return err.message;
  if (err instanceof Error && err.message.trim()) return err.message.trim();
  return fallback;
}

export interface LiquidBrokersCredentials {
  // Basic Authentication (username/password)
  username?: string;
  domain?: string;
  password?: string;
  
  // HMAC Authentication (API tokens)
  apiPrincipal?: string; // Public API token
  apiSecret?: string;    // Private API token
  
  // Account information
  accountCode?: string; // Format: "clearing:account" (e.g., "default:NOC_978195_1" for LIVE, "default:DEM_978195_4" for DEMO)
  customName?: string | null; // Optional display name from GET /broker/credentials
  /** Import-lane placeholder — no live API sync (CSV import only). */
  csv_import_only?: boolean;
  /** Real API credentials stored — Broker Sync endpoints work. */
  supports_live_sync?: boolean;

  // API endpoint (optional, defaults to fxblue endpoint)
  apiEndpoint?: string; // e.g., "https://trader.liquidcharts.com/dxsca-fxblue"
  
  // Session management (optional, for maintaining connection)
  sessionTimeout?: number; // Session timeout in seconds (default: 600)
  pingInterval?: number;   // Ping interval in seconds (default: 300, usually 0.5 * timeout)
}

// Legacy interface support (for backward compatibility)
export interface LegacyCredentials {
  apiKey: string;
  apiSecret: string;
  accountId?: string;
}

export interface LiquidBrokersConnectionStatus {
  connected: boolean;
  broker: string;
  accountCode?: string;
  lastSync?: Date;
  error?: string;
}

// DXtrade API Response Types
interface DXtradeLoginResponse {
  sessionToken?: string;
  timeout?: string;
}

interface DXtradeOrder {
  account: string;
  orderId: number;
  orderCode: string;
  clientOrderId: string;
  instrument: string;
  side: "BUY" | "SELL";
  type: "MARKET" | "LIMIT" | "STOP";
  status: "ACCEPTED" | "WORKING" | "CANCELED" | "COMPLETED" | "EXPIRED" | "REJECTED";
  finalStatus: boolean;
  issueTime: string; // RFC 3339 timestamp
  transactionTime: string; // RFC 3339 timestamp
  legs: Array<{
    instrument: string;
    quantity: number;
    filledQuantity: number;
    remainingQuantity: number;
    price: number;
    averagePrice: number;
  }>;
  executions: Array<{
    executionCode: string;
    status: string;
    filledQuantity: number;
    lastQuantity: number;
    lastPrice?: number;
    averagePrice?: number;
    transactionTime: string;
  }>;
  cashTransactions?: Array<{
    type: string;
    value: number;
    currency: string;
    transactionTime: string;
  }>;
}

interface DXtradeHistoricOrderList {
  orders: DXtradeOrder[];
  nextPageTransactionTime?: string;
}

interface DXtradeAccountDetails {
  owner: string;
  account: string; // Account code format: "clearing:account"
  version: number;
  baseCurrency: string;
  registrationTime: string;
  accountStatus: "FULL_TRADING" | "CLOSE_ONLY" | "NO_TRADING" | "TERMINATED" | "EXPIRED";
  pricingStream: string;
  isPositionBased?: boolean;
}

interface DXtradeUserDetails {
  login: string;
  domain: string;
  username: string;
  version: number;
  fullName: string;
  accounts: DXtradeAccountDetails[];
}

export interface BrokerAccount {
  accountCode: string;
  accountStatus: string;
  baseCurrency: string;
  isDemo?: boolean; // Determined by account name/code patterns
  balance?: number; // Portfolio/account balance when available
  todayPnl?: number; // Today's P&L when available from backend
  customName?: string | null; // Optional display name from GET /broker/accounts
}

/**
 * Format account for display (full: e.g. "My Demo (default:DEM_123)").
 * Rule: customName ? "customName (accountCode)" : accountCode; append " (Demo)" when isDemo.
 */
export function formatAccountDisplayLabel(account: {
  accountCode: string;
  customName?: string | null;
  isDemo?: boolean;
}): string {
  const base = account.customName?.trim()
    ? `${account.customName.trim()} (${account.accountCode})`
    : account.accountCode;
  return account.isDemo ? `${base} (Demo)` : base;
}

/**
 * Short label for sidebar/compact UI: custom name only when set, else account code. No "(accountCode)" when custom name exists.
 */
export function formatAccountDisplayLabelShort(account: {
  accountCode: string;
  customName?: string | null;
  isDemo?: boolean;
}): string {
  const base = account.customName?.trim() ? account.customName.trim() : account.accountCode;
  return account.isDemo ? `${base} (Demo)` : base;
}

/**
 * Primary account label for sidebar pickers: custom name, else trade label, else account code.
 * Matches Broker Management / account selector naming (custom names are not suffixed with the code).
 */
export function formatTradeAccountPrimaryLabel(account: {
  accountCode: string;
  customName?: string | null;
  tradeLabel?: string | null;
  isDemo?: boolean;
}): string {
  const custom = account.customName?.trim();
  if (custom) return custom;
  const base = account.tradeLabel?.trim() || account.accountCode;
  return account.isDemo ? `${base} (Demo)` : base;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function stripKnownLabelPrefix(label: string, prefixes: string[]): string {
  const trimmed = label.trim();
  for (const prefix of prefixes) {
    const p = prefix.trim();
    if (!p) continue;
    const pattern = new RegExp(`^${escapeRegExp(p)}\\s*[–-]\\s*`, "i");
    if (pattern.test(trimmed)) {
      const rest = trimmed.replace(pattern, "").trim();
      if (rest) return rest;
    }
  }
  return trimmed;
}

/**
 * Account label when the broker is already shown in the parent row (nested sidebar panel).
 * Strips redundant broker prefixes from trade labels (e.g. "Binance – U123" → "U123").
 */
export function formatNestedBrokerAccountLabel(
  account: {
    accountCode: string;
    brokerName?: string;
    brokerDisplayName?: string;
    customName?: string | null;
    tradeLabel?: string | null;
    isDemo?: boolean;
  },
  brokerGroupLabel: string
): string {
  const custom = account.customName?.trim();
  if (custom) return custom;

  const prefixes = [
    brokerGroupLabel,
    account.brokerDisplayName,
    account.brokerName,
  ].filter((value): value is string => Boolean(value?.trim()));

  const base = account.tradeLabel?.trim()
    ? stripKnownLabelPrefix(account.tradeLabel.trim(), prefixes)
    : account.accountCode;

  return account.isDemo ? `${base} (Demo)` : base;
}

/**
 * Get Clerk authentication token
 * This function gets the token from Clerk's session
 * Note: This requires Clerk to be initialized in the app
 */
const getAuthToken = async (): Promise<string | null> => {
  try {
    // Try to get token from Clerk if available
    // This requires Clerk to be initialized in the app
    if (typeof window !== 'undefined') {
      // Try modern Clerk API (v5+)
      if ((window as any).Clerk) {
        const clerk = (window as any).Clerk;
        if (clerk.session) {
          const token = await clerk.session.getToken();
          return token;
        }
      }
      // Fallback: Try to get from Clerk React context if available
      // Components should pass token directly when possible
    }
    return null;
  } catch (error) {
    console.error("Failed to get Clerk token:", error);
    return null;
  }
};

/**
 * Helper for authenticated API calls to backend
 */
const apiCall = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  const token = await getAuthToken();
  const headers = {
      "Content-Type": "application/json",
      "Accept": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  // BACKEND_API_URL already includes /api, so endpoints should not include /api prefix
  return fetch(`${BACKEND_API_URL}${endpoint}`, {
    ...options,
    headers,
  });
};

/**
 * REMOVED: createSessionToken - Authentication now handled by backend
 * All broker API calls are routed through the FastAPI backend
 */

// Removed createSessionToken function - backend handles authentication
// Removed getAuthHeader function - backend handles authentication

/**
 * @deprecated Prefer GET /broker/trade-accounts for the account picker.
 * Only call from the Liquid Brokers live connect flow — never on app load.
 */
export const fetchBrokerAccounts = async (
  credentials?: LiquidBrokersCredentials
): Promise<{ accounts: BrokerAccount[]; error?: string }> => {
  try {
    const response = await apiCall("/broker/accounts?broker_name=liquidbrokers");

    if (!response.ok) {
      await throwLiquidBrokersResponseNotOk(
        response,
        `Failed to fetch accounts: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    const raw = Array.isArray(data) ? data : (data?.accounts && Array.isArray(data.accounts) ? data.accounts : []);
    const list = raw.map((a: any) => ({
      ...a,
      customName: a.customName ?? a.custom_name ?? null,
    }));
    return { accounts: list };
  } catch (error: unknown) {
    return {
      accounts: [],
      error: liquidBrokersErrorMessage(error, "Failed to fetch broker accounts"),
    };
  }
};

/**
 * REMOVED: fetchAccountBalance - Now included in fetchBrokerAccounts response
 * Balance is fetched by backend and included in account objects
 */

/**
 * Fetch broker account snapshot (same shape as WebSocket account_update.data).
 * Use on page load with refresh=true to get up-to-date broker-backed metrics.
 * GET /api/broker/account/snapshot
 */
export const getAccountSnapshot = async (options: {
  refresh?: boolean;
  broker_name: string;
}): Promise<AccountSnapshot> => {
  const refresh = options.refresh ?? false;
  const brokerName = options.broker_name.trim();
  if (!brokerName) {
    throwLiquidBrokersClientError("broker_name is required for account snapshot");
  }
  const params = new URLSearchParams();
  params.set("refresh", String(refresh));
  params.set("broker_name", brokerName);
  const response = await apiCall(`/broker/account/snapshot?${params.toString()}`);

  if (!response.ok) {
    const fallback =
      response.status === 404
        ? "No cached snapshot yet; use ?refresh=true to build one now."
        : response.status === 502
          ? "Broker metrics request failed."
          : `Failed to fetch account snapshot: ${response.status}`;
    await throwLiquidBrokersResponseNotOk(response, fallback);
  }

  return parseJsonOrThrow<AccountSnapshot>(response, `Failed to fetch account snapshot: ${response.status}`);
};

/**
 * Test connection to LiquidBrokers API
 * POST /broker/test-connection
 */
export const testLiquidBrokersConnection = async (
  credentials: LiquidBrokersCredentials
): Promise<BrokerTestConnectionResult & { accountInfo?: any; accounts?: BrokerAccount[] }> => {
  try {
    const response = await apiCall("/broker/test-connection", {
      method: "POST",
      body: JSON.stringify({
        broker_name: "liquidbrokers",
        username: credentials.username,
        password: credentials.password,
        domain: credentials.domain || "default",
        api_endpoint: credentials.apiEndpoint,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const body = errorData && typeof errorData === "object" ? errorData : {};
      const parsed = parseApiErrorBody(body, "Failed to test connection");
      return {
        success: false,
        message: parsed.message || "Failed to test connection",
        action: parsed.action,
        error: parsed.slug,
      };
    }
    
    const data = await response.json();
      return {
      success: data.success,
      message: data.message,
      action: typeof data.action === "string" ? data.action : undefined,
      error: typeof data.error === "string" ? data.error : undefined,
      accounts: data.accounts,
      accountInfo: data.accountInfo,
    };
  } catch (error: unknown) {
    return {
      success: false,
      message: liquidBrokersErrorMessage(error, "Failed to connect to LiquidBrokers API"),
    };
  }
};

/**
 * REMOVED: All old proxy-related functions
 * - createSessionToken: Removed (backend handles authentication)
 * - getAuthHeader: Removed (backend handles authentication)
 * - getApiBaseUrl: Removed (backend handles API endpoints)
 * - fetchAccountBalance: Removed (balance included in accounts response)
 */

/**
 * REMOVED: fetchAccountBalance
 * Balance is now included in the accounts response from the backend
 * No need for a separate balance fetch function
 */

/**
 * Test connection to LiquidBrokers API
 */
// testLiquidBrokersConnection already updated above - this is a duplicate, removing

/**
 * Parse broker CSV date format: "2025/12/08 14:24:46"
 */
const parseBrokerDate = (dateStr: string): Date | null => {
  if (!dateStr || dateStr.trim() === "") return null;
  
  const match = dateStr.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})\s+(\d{1,2}):(\d{1,2}):(\d{1,2})$/);
  if (match) {
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;
    const day = parseInt(match[3], 10);
    const hour = parseInt(match[4], 10);
    const minute = parseInt(match[5], 10);
    const second = parseInt(match[6], 10);
    return new Date(year, month, day, hour, minute, second);
  }
  
  const parsed = Date.parse(dateStr);
  if (!isNaN(parsed)) {
    return new Date(parsed);
  }
  
  return null;
};

/**
 * Convert broker market symbol to standard format
 */
const normalizeSymbol = (market: string): string => {
  if (!market) return "";
  if (market.endsWith("USD")) {
    const base = market.slice(0, -3);
    return `${base}/USD`;
  }
  return market;
};

/**
 * Convert quantity from broker units to standard lots based on symbol type
 * @param quantity - Quantity in broker units
 * @param symbol - Symbol (e.g., "XAU/USD", "EUR/USD")
 * @returns Quantity in standard lots
 */
const convertQuantityToLots = (quantity: number, symbol: string): number => {
  const symbolUpper = symbol.toUpperCase();
  
  // Gold (XAU): 1 standard lot = 100 oz
  // If API returns in ounces, convert: 1 oz = 0.01 lots
  if (symbolUpper.includes("XAU") || symbolUpper.includes("GOLD")) {
    return quantity / 100;
  }
  
  // Forex: 1 standard lot = 100,000 units
  // If API returns in units, convert: 1 unit = 0.00001 lots
  // For now, assume forex quantities are already in lots or need different handling
  // Return as-is for other instruments (may need adjustment based on actual API behavior)
  return quantity;
};

/**
 * Get multiplier to convert lots back to units for calculations
 * @param symbol - Symbol (e.g., "XAU/USD", "EUR/USD")
 * @returns Multiplier (e.g., 100 for gold, 100000 for forex)
 */
const getLotsToUnitsMultiplier = (symbol: string): number => {
  const symbolUpper = symbol.toUpperCase();
  
  // Gold (XAU): 1 lot = 100 oz
  if (symbolUpper.includes("XAU") || symbolUpper.includes("GOLD")) {
    return 100;
  }
  
  // Forex: 1 lot = 100,000 units
  // For now, return 1 for other instruments (may need adjustment)
  return 1;
};

/**
 * Parse broker CSV row to Trade object
 */
const parseBrokerRow = (row: string[], headers: string[], userId: string): Trade | null => {
  const headerMap: { [key: string]: number } = {};
  headers.forEach((header, index) => {
    headerMap[header.trim()] = index;
  });

  const getValue = (headerName: string): string => {
    const index = headerMap[headerName];
    return index !== undefined && index < row.length ? (row[index] || "").trim() : "";
  };

  const action = getValue("Action");
  const market = getValue("Market");
  
  if (action !== "Buy" && action !== "Sell") {
    return null;
  }

  const id = getValue("ID") || `trade-${Date.now()}-${Math.random()}`;
  const lots = parseFloat(getValue("Lots")) || 0;
  const openTimeStr = getValue("Open time");
  const openPrice = parseFloat(getValue("Open price")) || 0;
  const closeTimeStr = getValue("Close Time");
  const closePrice = parseFloat(getValue("Close price")) || 0;
  const netProfit = parseFloat(getValue("Net profit")) || 0;
  const commission = parseFloat(getValue("Commission")) || 0;
  const returnPercent = parseFloat(getValue("% return")) || 0;

  const entryDate = parseBrokerDate(openTimeStr);
  const exitDate = closeTimeStr ? parseBrokerDate(closeTimeStr) : null;

  if (!entryDate || !market) {
    return null;
  }

  const status: "open" | "closed" = exitDate ? "closed" : "open";
  const type: "buy" | "sell" = action.toLowerCase() === "buy" ? "buy" : "sell";

  const entryQuantity = lots;
  const entryValue = openPrice * entryQuantity;
  const entryFees = commission / 2;
  const exitFees = commission / 2;

  let exitPrice: number | undefined;
  let exitQuantity: number | undefined;
  let exitValue: number | undefined;
  let grossPnL: number | undefined;
  let netPnL: number | undefined;
  let roi: number | undefined;

  if (status === "closed" && exitDate) {
    exitPrice = closePrice;
    exitQuantity = entryQuantity;
    exitValue = exitPrice * exitQuantity;
    
    if (netProfit !== null && netProfit !== undefined && !isNaN(netProfit)) {
      netPnL = netProfit;
      grossPnL = netPnL + commission;
    } else {
      if (type === "buy") {
        grossPnL = (exitPrice - openPrice) * entryQuantity;
      } else {
        grossPnL = (openPrice - exitPrice) * entryQuantity;
      }
      netPnL = grossPnL - commission;
    }
    
    if (returnPercent !== 0 && returnPercent !== 1) {
      roi = (returnPercent - 1) * 100;
    } else if (entryValue > 0) {
      roi = (netPnL! / entryValue) * 100;
    }
  }

  return {
    id,
    symbol: normalizeSymbol(market),
    type,
    status,
    entryDate: entryDate.toISOString(),
    entryPrice: openPrice,
    entryQuantity,
    entryFees,
    entryValue,
    exitDate: exitDate ? exitDate.toISOString() : undefined,
    exitPrice,
    exitQuantity,
    exitFees: status === "closed" ? exitFees : undefined,
    exitValue,
    grossPnL,
    netPnL,
    returnPercent: roi,
    roi,
    strategy: undefined,
    notes: undefined,
    tags: ["broker-sync"],
    attachments: [],
    createdAt: entryDate.toISOString(),
    updatedAt: exitDate ? exitDate.toISOString() : entryDate.toISOString(),
    userId,
  };
};

/**
 * Match BUY and SELL orders to create complete trades
 * Uses FIFO (First In, First Out) matching
 */
const matchOrdersToTrades = (
  orders: DXtradeOrder[],
  userId: string
): Trade[] => {
  const trades: Trade[] = [];
  
  // Group orders by instrument
  const ordersByInstrument = new Map<string, DXtradeOrder[]>();
  orders.forEach((order) => {
    if (!ordersByInstrument.has(order.instrument)) {
      ordersByInstrument.set(order.instrument, []);
    }
    ordersByInstrument.get(order.instrument)!.push(order);
  });
  
  // Process each instrument separately
  ordersByInstrument.forEach((instrumentOrders, symbol) => {
    // Separate BUY and SELL orders
    const buyOrders: Array<{ order: DXtradeOrder; quantity: number; quantityInUnits?: number; price: number; date: Date; commission: number }> = [];
    const sellOrders: Array<{ order: DXtradeOrder; quantity: number; quantityInUnits?: number; price: number; date: Date; commission: number }> = [];
    
    instrumentOrders.forEach((order) => {
      if (order.status !== "COMPLETED" || !order.finalStatus) {
        return;
      }
      
      const executions = order.executions.filter(
        (exec) => exec.status === "COMPLETED" && exec.lastQuantity !== 0 && exec.lastPrice
      );
      
      if (executions.length === 0) {
        return;
      }
      
      // Calculate total filled quantity and weighted average price
      let totalQuantity = 0;
      let totalValue = 0;
      let totalCommission = 0;
      
      executions.forEach((exec) => {
        const qty = Math.abs(exec.lastQuantity);
        totalQuantity += qty;
        if (exec.lastPrice) {
          totalValue += exec.lastPrice * qty;
        }
      });
      
      const avgPrice = totalQuantity > 0 ? totalValue / totalQuantity : 0;
      
      // Calculate commission
      if (order.cashTransactions) {
        order.cashTransactions.forEach((tx) => {
          if (tx.type === "COMMISSION") {
            totalCommission += Math.abs(tx.value);
          }
        });
      }
      
      // Convert quantity to standard lots based on symbol type
      const quantityInLots = convertQuantityToLots(totalQuantity, symbol);
      
      const orderData = {
        order,
        quantity: quantityInLots, // Store in lots for display
        quantityInUnits: totalQuantity, // Keep original units for calculations
        price: avgPrice,
        date: new Date(order.issueTime),
        commission: totalCommission,
      };
      
      if (order.side === "BUY") {
        buyOrders.push(orderData);
      } else {
        sellOrders.push(orderData);
      }
    });
    
    // Sort by date (FIFO)
    buyOrders.sort((a, b) => a.date.getTime() - b.date.getTime());
    sellOrders.sort((a, b) => a.date.getTime() - b.date.getTime());
    
    // Match BUY and SELL orders using FIFO
    let buyIndex = 0;
    let sellIndex = 0;
    
    while (buyIndex < buyOrders.length && sellIndex < sellOrders.length) {
      const buy = buyOrders[buyIndex];
      const sell = sellOrders[sellIndex];
      
      // Determine trade quantity in lots (minimum of buy and sell quantities)
      const tradeQuantityInLots = Math.min(buy.quantity, sell.quantity);
      
      // Get quantities in units for calculations (P&L, entryValue, etc.)
      // Use original units if available, otherwise convert back from lots
      const buyQuantityInUnits = buy.quantityInUnits || (buy.quantity * getLotsToUnitsMultiplier(symbol));
      const sellQuantityInUnits = sell.quantityInUnits || (sell.quantity * getLotsToUnitsMultiplier(symbol));
      const tradeQuantityInUnits = Math.min(buyQuantityInUnits, sellQuantityInUnits);
      
      if (tradeQuantityInLots > 0) {
        // Calculate P&L using units (correct for gold/forex calculations)
        const grossPnL = (sell.price - buy.price) * tradeQuantityInUnits;
        const totalCommission = buy.commission + sell.commission;
        const netPnL = grossPnL - totalCommission;
        
        const entryValue = buy.price * tradeQuantityInUnits;
        const exitValue = sell.price * tradeQuantityInUnits;
        const roi = entryValue > 0 ? (netPnL / entryValue) * 100 : 0;
        
        // Create trade (entryQuantity stored in lots for display)
        trades.push({
          id: `dx-${buy.order.orderId}-${sell.order.orderId}`,
          symbol,
          type: "buy", // Always "buy" since we're matching BUY->SELL
          status: "closed",
          entryDate: buy.date.toISOString(),
          entryPrice: buy.price,
          entryQuantity: tradeQuantityInLots, // Store in lots for display
          entryFees: buy.commission,
          entryValue,
          exitDate: sell.date.toISOString(),
          exitPrice: sell.price,
          exitQuantity: tradeQuantityInLots, // Store in lots for display
          exitFees: sell.commission,
          exitValue,
          grossPnL,
          netPnL,
          returnPercent: roi,
          roi,
          strategy: undefined,
          notes: `Buy Order: ${buy.order.orderId}, Sell Order: ${sell.order.orderId}`,
          tags: ["broker-sync", "liquidbrokers"],
          attachments: [],
          createdAt: buy.date.toISOString(),
          updatedAt: sell.date.toISOString(),
          userId,
        });
        
        // Reduce quantities (in lots)
        buy.quantity -= tradeQuantityInLots;
        sell.quantity -= tradeQuantityInLots;
        
        // Also reduce quantities in units for accurate matching
        if (buy.quantityInUnits !== undefined) {
          buy.quantityInUnits -= tradeQuantityInUnits;
        }
        if (sell.quantityInUnits !== undefined) {
          sell.quantityInUnits -= tradeQuantityInUnits;
        }
        
        // Move to next order if current one is fully matched
        if (buy.quantity <= 0) {
          buyIndex++;
        }
        if (sell.quantity <= 0) {
          sellIndex++;
        }
      } else {
        // No match possible, move to next
        if (buy.date < sell.date) {
          buyIndex++;
        } else {
          sellIndex++;
        }
      }
    }
  });
  
  return trades;
};

/**
 * Convert DXtrade Order to Trade object (legacy - kept for backward compatibility)
 * NOTE: This treats each order as a separate trade, which is incorrect.
 * Use matchOrdersToTrades() instead for accurate P&L calculation.
 */
const convertDXtradeOrderToTrade = (
  order: DXtradeOrder,
  userId: string
): Trade | null => {
  // This function is deprecated - orders should be matched, not converted individually
  return null;
};

/**
 * Fetch trades from LiquidBrokers API using DXtrade REST API
 * GET/POST /accounts/{account code}/orders/history
 */
/**
 * Fetch trades from LiquidBrokers API via backend
 * GET /broker/trades
 */
export const fetchLiquidBrokersTrades = async (
  credentials: LiquidBrokersCredentials,
  userId: string,
  startDate?: Date,
  endDate?: Date
): Promise<{ trades: Trade[]; error?: string }> => {
  try {
    const params = new URLSearchParams();
    params.append("broker_name", "liquidbrokers");
    if (startDate) params.append("start_date", startDate.toISOString());
    if (endDate) params.append("end_date", endDate.toISOString());
    if (credentials.accountCode) params.append("account_code", credentials.accountCode);
    
    const response = await apiCall(`/broker/trades?${params.toString()}`);
    
    if (!response.ok) {
      await throwLiquidBrokersResponseNotOk(
        response,
        `Failed to fetch trades: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    const orders = data.orders || [];
    const trades = matchOrdersToTrades(orders, userId);

    console.log(`[API] Matched ${orders.length} orders into ${trades.length} complete trades`);

    return {
      trades,
      error: undefined,
    };
  } catch (error: unknown) {
    console.error("Error fetching LiquidBrokers trades:", error);
    return {
      trades: [],
      error: liquidBrokersErrorMessage(error, "Failed to fetch trades from LiquidBrokers API"),
    };
  }
};

/**
 * Save broker credentials to backend (encrypted)
 * POST /broker/credentials
 */
export const saveBrokerCredentials = async (
  broker: string,
  credentials: LiquidBrokersCredentials
): Promise<void> => {
  try {
    await apiCall("/broker/credentials", {
      method: "POST",
      body: JSON.stringify({
        broker_name: broker.toLowerCase(),
        username: credentials.username,
        password: credentials.password,
        domain: credentials.domain || "default",
        api_endpoint: credentials.apiEndpoint,
      }),
    });
  } catch (error) {
    console.error("Failed to save broker credentials:", error);
    throw error;
  }
};

/**
 * Get saved broker credentials from backend (non-sensitive info only)
 * GET /broker/credentials
 */
export const getBrokerCredentials = async (
  broker: string
): Promise<LiquidBrokersCredentials | null> => {
  try {
    const response = await apiCall(`/broker/credentials?broker_name=${broker.toLowerCase()}`);
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return {
      apiEndpoint: data.api_endpoint,
      accountCode: data.account_code,
      customName: data.custom_name ?? data.customName ?? null,
      // Note: Backend doesn't return sensitive fields (username, password)
    };
  } catch (error) {
    console.error("Failed to get broker credentials:", error);
    return null;
  }
};

/**
 * @deprecated Use `updateBrokerAccountCustomName` from `brokerIntegrationApi` (same contract).
 */
export { updateBrokerAccountCustomName } from "./brokerIntegrationApi";

/**
 * Update account code for broker credentials
 * PUT /broker/credentials/account
 */
export const updateBrokerAccountCode = async (
  broker: string,
  accountCode: string
): Promise<void> => {
  try {
    await apiCall(`/broker/credentials/account?broker_name=${broker.toLowerCase()}&account_code=${encodeURIComponent(accountCode)}`, {
      method: "PUT",
    });
  } catch (error) {
    console.error("Failed to update account code:", error);
    throw error;
  }
};

/**
 * Get broker connection status
 * GET /broker/credentials
 */
export const getBrokerConnectionStatus = async (
  broker: string
): Promise<LiquidBrokersConnectionStatus> => {
  try {
    const credentials = await getBrokerCredentials(broker);
    // Backend should return lastSync in connection status, not in credentials
    // For now, we'll get it from a separate endpoint or it will be undefined
  return {
    connected: !!credentials,
    broker,
    accountCode: credentials?.accountCode,
      // lastSync will be updated by backend when trades are synced
      // TODO: Add endpoint to get lastSync timestamp
    };
  } catch {
    return {
      connected: false,
      broker,
  };
  }
};

/**
 * REMOVED: saveLastSyncTimestamp - Backend handles this automatically
 * Last sync is updated by the backend when trades are fetched
 */

/**
 * Remove broker credentials
 * DELETE /broker/credentials
 */
export const removeBrokerCredentials = async (broker: string): Promise<void> => {
  try {
    await apiCall(`/broker/credentials?broker_name=${broker.toLowerCase()}`, {
      method: "DELETE",
    });
  } catch (error) {
    console.error("Failed to remove broker credentials:", error);
    throw error;
  }
};

/**
 * System Account interface for admin view
 */
export interface SystemAccount {
  clerk_user_id: string;
  username: string;
  email?: string;
  account_code?: string;
  portfolio_size?: number;
  status?: "active" | "inactive" | "connected" | "disconnected" | string;
  error?: string | null;
}

/**
 * Fetch system accounts (admin only)
 * GET /broker/admin/system-accounts
 */
export const fetchSystemAccounts = async (
  brokerName: string = "liquidbrokers"
): Promise<SystemAccount[]> => {
  const endpoint = `/broker/admin/system-accounts?broker_name=${brokerName}`;
  const response = await apiCall(endpoint);

  if (!response.ok) {
    if (response.status === 403) {
      throw new ApiRequestError(
        "Admin access required. Please ensure your user has admin privileges.",
        403,
        {
          slug: "auth.forbidden",
          message: "Admin access required. Please ensure your user has admin privileges.",
        }
      );
    }
    if (response.status === 401) {
      throw new ApiRequestError("Authentication failed. Please sign in again.", 401, {
        slug: "auth.token_expired",
        message: "Authentication failed. Please sign in again.",
      });
    }
    await throwLiquidBrokersResponseNotOk(
      response,
      `Failed to fetch system accounts: ${response.status} ${response.statusText}`
    );
  }

  return (await response.json()) as SystemAccount[];
};

/**
 * Start a system account (admin only)
 * POST /broker/admin/system-accounts/{clerk_user_id}/start
 */
export const startSystemAccount = async (
  clerkUserId: string,
  brokerName: string = "liquidbrokers"
): Promise<{ message: string; is_active: boolean; account_code?: string }> => {
  const response = await apiCall(
    `/broker/admin/system-accounts/${clerkUserId}/start?broker_name=${brokerName}`,
    { method: "POST" }
  );

  if (!response.ok) {
    await throwLiquidBrokersResponseNotOk(
      response,
      `Failed to start account: ${response.status} ${response.statusText}`
    );
  }

  return response.json();
};

/**
 * Stop a system account (admin only)
 * POST /broker/admin/system-accounts/{clerk_user_id}/stop
 */
export const stopSystemAccount = async (
  clerkUserId: string,
  brokerName: string = "liquidbrokers"
): Promise<{ message: string; is_active: boolean; account_code?: string }> => {
  const response = await apiCall(
    `/broker/admin/system-accounts/${clerkUserId}/stop?broker_name=${brokerName}`,
    { method: "POST" }
  );

  if (!response.ok) {
    await throwLiquidBrokersResponseNotOk(
      response,
      `Failed to stop account: ${response.status} ${response.statusText}`
    );
  }

  return response.json();
};

/**
 * Close all positions for a system account (admin only)
 * POST /broker/admin/system-accounts/{account_name}/close-all-positions
 * Note: Uses account name (e.g., "RANDOMFOREST", "Arbiter") not clerk_user_id
 */
export interface CloseAllPositionsResponse {
  message: string;
  account_code?: string;
  positions_closed: number;
  positions_failed: number;
  total_positions: number;
  failed_positions?: any;
}

export const closeAllPositionsForAccount = async (
  accountName: string,
  brokerName: string = "liquidbrokers"
): Promise<CloseAllPositionsResponse> => {
  const response = await apiCall(
    `/broker/admin/system-accounts/${accountName}/close-all-positions?broker_name=${brokerName}`,
    { method: "POST" }
  );

  if (!response.ok) {
    await throwLiquidBrokersResponseNotOk(
      response,
      `Failed to close positions: ${response.status} ${response.statusText}`
    );
  }

  return (await response.json()) as CloseAllPositionsResponse;
};


